import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    deleteSimilarityEdge,
    reviewSimilarityEdge,
} from '../../../firebase/firestore/icr';
import {getUsersByIds} from '../../../firebase/firestore/users';
import PersonCell, {indexUsersById} from '../../collection/components/PersonCell';
import AddRelationDialog from './AddRelationDialog';
import SimilarityScoreBars from './SimilarityScoreBars';
import {
    TYPE_COLORS,
    coveragePairBadges,
    edgeSimilarityPercent,
    resolveEeiNavPath,
    resolveGapNavPath,
    resolveIcrNavPath,
    splitTargetTitle,
} from './similarityLabels';

function openPath(path) {
    if (!path) return;
    window.open(path, '_blank', 'noopener,noreferrer');
}

function edgeIdentity(edge) {
    return edge?.id
        || `${edge?.fromType}:${edge?.fromId}:${edge?.toType}:${edge?.toId}:${edge?.relation}`;
}

function SimilarityEdgeRow({edge, actions, busyKey, people, narrow = false}) {
    const target = edge?.target || {};
    const type = target.type || '—';
    const reviewStatus = edge?.reviewStatus || 'suggested';
    const source = edge?.source || 'machine';
    const isManual = source === 'manual';
    const isProposed = source === 'proposed';
    const isGapLink = Boolean(edge?.gapLink);
    const edgeKey = edgeIdentity(edge);
    const busy = busyKey === edgeKey;
    const commentText = edge?.comment || '';
    const similarityPercent = edgeSimilarityPercent(edge);
    const coverageBadges = coveragePairBadges(target.coverage, type);
    const typeColor = TYPE_COLORS[type] || '#757575';

    const attributionUserId = isManual
        ? edge?.createdBy
        : (reviewStatus === 'confirmed' ? edge?.reviewedBy : null);
    const attributionPrefix = isManual
        ? 'added by'
        : (reviewStatus === 'confirmed' && edge?.reviewedBy ? 'confirmed by' : null);
    const reportAuthorIds = (Array.isArray(target.reportAuthorIds) ? target.reportAuthorIds : [])
        .filter(Boolean)
        .map(String);

    const onOpen = () => {
        if (type === 'ICR') openPath(resolveIcrNavPath(target));
        else if (type === 'EEI') openPath(resolveEeiNavPath(target));
        else if (type === 'GAP') openPath(resolveGapNavPath(target));
    };

    const clickable = type === 'ICR' || type === 'EEI'
        || (type === 'GAP' && Boolean(target.number || target.gapNumber || target.id));

    const review = (status) => actions?.review?.({
        fromType: edge.fromType,
        fromId: edge.fromId,
        toType: edge.toType,
        toId: edge.toId,
        relation: edge.relation,
        status,
        edgeKey,
    });

    const canReview = Boolean(actions) && !isManual && !isProposed && reviewStatus === 'suggested';
    const canDelete = Boolean(actions) && !isProposed && Boolean(edge?.id)
        && ((isGapLink && actions?.unlinkGapLink) || (!isGapLink && (isManual || reviewStatus === 'confirmed')));

    const {number: entityNumber, text: entityText} = splitTargetTitle(target);

    const hasFooterContent = coverageBadges.length > 0
        || (attributionPrefix && attributionUserId)
        || (actions && (canReview || canDelete));

    return (
        <Paper
            variant="outlined"
            data-testid="similarity-edge-row"
            sx={{
                p: 1.75,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderRadius: 2,
                borderColor: 'divider',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                '&:hover': {
                    borderColor: typeColor,
                    boxShadow: `0 1px 6px ${typeColor}18`,
                },
            }}
        >
            {/* Header: type + entity number + similarity % */}
            <Box
                data-testid="similarity-edge-header"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, overflow: 'hidden'}}>
                    <Chip
                        label={type}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            flexShrink: 0,
                            bgcolor: `${typeColor}18`,
                            color: typeColor,
                            minWidth: 40,
                            height: 22,
                            '& .MuiChip-label': {px: 0.75},
                        }}
                    />
                    {entityNumber && (
                        <Typography
                            variant="caption"
                            data-testid="similarity-edge-id"
                            sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: 'text.secondary',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {entityNumber}
                        </Typography>
                    )}
                </Box>
                <Box
                    data-testid="similarity-edge-badges"
                    sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}
                >
                    <SimilarityScoreBars percent={similarityPercent}/>
                    {clickable && (
                        <Tooltip title={`Open ${type}`} arrow placement="top">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpen();
                                }}
                                sx={{p: 0.25, color: 'text.disabled', '&:hover': {color: typeColor}}}
                            >
                                <OpenInNewIcon sx={{fontSize: '0.9rem'}}/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Body: question text + metadata */}
            <Stack spacing={0.5} data-testid="similarity-edge-body" sx={{minWidth: 0}}>
                <Typography
                    variant="body2"
                    data-testid="similarity-edge-title"
                    sx={{
                        lineHeight: 1.6,
                        color: 'text.primary',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {entityText}
                </Typography>

                {type === 'GAP' && target.reportTitle && (
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color="text.secondary">
                            FINTEL: {target.reportTitle}
                        </Typography>
                        {reportAuthorIds.length > 0 ? (
                            <>
                                <Typography variant="caption" color="text.secondary">by</Typography>
                                {reportAuthorIds.map((authorId) => (
                                    <PersonCell
                                        key={authorId}
                                        userId={authorId}
                                        profile={people?.[authorId]}
                                        size="small"
                                    />
                                ))}
                            </>
                        ) : target.reportAuthors ? (
                            <Typography variant="caption" color="text.secondary">
                                by {target.reportAuthors}
                            </Typography>
                        ) : null}
                    </Stack>
                )}

                {commentText ? (
                    <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        {commentText}
                    </Typography>
                ) : null}
            </Stack>

            {/* Footer: coverage badges + attribution + actions */}
            {hasFooterContent && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        mt: 0.25,
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                        {coverageBadges.length > 0 && (
                            <Box
                                data-testid="similarity-edge-coverage"
                                sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center'}}
                            >
                                {coverageBadges.map((b) => (
                                    <Chip
                                        key={b.key}
                                        label={b.label}
                                        size="small"
                                        color={b.tone === 'default' ? undefined : b.tone}
                                        variant="outlined"
                                        sx={{height: 20, fontSize: '0.7rem', '& .MuiChip-label': {px: 0.75}}}
                                    />
                                ))}
                            </Box>
                        )}
                        {attributionPrefix && attributionUserId && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="caption" color="text.disabled">
                                    {attributionPrefix}
                                </Typography>
                                <PersonCell
                                    userId={attributionUserId}
                                    profile={people?.[attributionUserId]}
                                    size="small"
                                />
                            </Stack>
                        )}
                    </Box>

                    {actions && (canReview || canDelete) && (
                        <Box
                            data-testid="similarity-edge-actions"
                            sx={{display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0}}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {canReview && (
                                <>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        disabled={busy}
                                        onClick={() => review('confirmed')}
                                        sx={{textTransform: 'none', minWidth: 0, px: 1, height: 22, fontSize: '0.7rem'}}
                                    >
                                        Confirm
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        disabled={busy}
                                        onClick={() => review('rejected')}
                                        sx={{textTransform: 'none', minWidth: 0, px: 1, height: 22, fontSize: '0.7rem'}}
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}
                            {canDelete && (
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={busy}
                                    aria-label="Delete link"
                                    onClick={() => {
                                        if (isGapLink) {
                                            actions?.unlinkGapLink?.(edge, edgeKey);
                                        } else {
                                            actions?.remove?.(edge, edgeKey);
                                        }
                                    }}
                                    sx={{p: 0.25}}
                                >
                                    <DeleteOutlineIcon sx={{fontSize: '0.95rem'}}/>
                                </IconButton>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
}

function flattenEdges(edges, byRelation) {
    if (Array.isArray(edges)) return edges;
    if (byRelation && typeof byRelation === 'object') {
        return Object.values(byRelation).flat();
    }
    return [];
}

/**
 * Flat list of similarity edges with Confirm/Reject, delete, coverage badges,
 * and optional Add Relation at the bottom.
 */
export default function SimilarityRelationList({
    edges,
    byRelation,
    dense = false,
    onEdgeReviewed,
    onUnlinkGapLink,
    allowReview = true,
    currentUserId,
    subject,
    showAddRelation = false,
}) {
    const [busyKey, setBusyKey] = useState(null);
    const [error, setError] = useState(null);
    const [people, setPeople] = useState({});
    const [addOpen, setAddOpen] = useState(false);

    const flatEdges = useMemo(
        () => flattenEdges(edges, byRelation),
        [edges, byRelation],
    );

    const peopleIdsKey = useMemo(() => {
        const ids = new Set();
        for (const e of flatEdges) {
            if (e?.source === 'manual' && e?.createdBy) ids.add(e.createdBy);
            if (e?.source !== 'manual' && e?.reviewStatus === 'confirmed' && e?.reviewedBy) {
                ids.add(e.reviewedBy);
            }
            const authorIds = e?.target?.reportAuthorIds;
            if (Array.isArray(authorIds)) {
                for (const id of authorIds) {
                    if (id) ids.add(String(id));
                }
            }
        }
        return [...ids].sort().join('|');
    }, [flatEdges]);

    useEffect(() => {
        if (!peopleIdsKey) {
            setPeople({});
            return undefined;
        }
        const ids = peopleIdsKey.split('|');
        let cancelled = false;
        getUsersByIds(ids)
            .then((users) => {
                if (!cancelled) setPeople(indexUsersById(users || []));
            })
            .catch(() => {
                if (!cancelled) setPeople({});
            });
        return () => {
            cancelled = true;
        };
    }, [peopleIdsKey]);

    const withBusy = async (edgeKey, fn) => {
        setError(null);
        setBusyKey(edgeKey);
        try {
            await fn();
            if (onEdgeReviewed) {
                await onEdgeReviewed();
            }
        } catch (e) {
            setError(e?.message || 'Operation failed');
        } finally {
            setBusyKey(null);
        }
    };

    const actions = allowReview ? {
        review: (payload) => withBusy(payload.edgeKey, () => reviewSimilarityEdge({
            fromType: payload.fromType,
            fromId: payload.fromId,
            toType: payload.toType,
            toId: payload.toId,
            relation: payload.relation,
            status: payload.status,
        })),
        remove: (edge, edgeKey) => withBusy(edgeKey, () => deleteSimilarityEdge(edge.id)),
        unlinkGapLink: onUnlinkGapLink
            ? (edge, edgeKey) => withBusy(edgeKey, () => onUnlinkGapLink(edge))
            : null,
    } : null;

    return (
        <Stack spacing={dense ? 1 : 1.5}>
            {error && (
                <Typography variant="caption" color="error">{error}</Typography>
            )}

            {flatEdges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No related requirements yet. Relations appear after embeddings are available for other Active ICRs/EEIs/Gaps, or add a relation.
                </Typography>
            ) : (
                <Stack spacing={1}>
                    {flatEdges.map((edge, idx) => (
                        <SimilarityEdgeRow
                            key={`${edge?.target?.type}:${edge?.target?.id}:${idx}`}
                            edge={edge}
                            busyKey={busyKey}
                            actions={actions}
                            people={people}
                            narrow={dense}
                        />
                    ))}
                </Stack>
            )}

            {showAddRelation && subject?.type && subject?.id && (
                <>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAddOpen(true)}
                        sx={{textTransform: 'none', alignSelf: 'flex-start', px: 1, height: 24, fontSize: '0.75rem'}}
                    >
                        Add Relation
                    </Button>
                    <AddRelationDialog
                        open={addOpen}
                        subject={subject}
                        existingEdges={flatEdges}
                        onClose={() => setAddOpen(false)}
                        onCreated={onEdgeReviewed}
                    />
                </>
            )}
        </Stack>
    );
}
